import React, { useState } from "react";
import { useSelector } from "react-redux";
import { ProSidebar, Menu, MenuItem } from "react-pro-sidebar";
import { Box, IconButton, Tooltip, Typography, useTheme, useMediaQuery } from "@mui/material";
import { Link } from "react-router-dom";
import "react-pro-sidebar/dist/css/styles.css";
import { tokens } from "../../theme";
import HomeOutlinedIcon from "@mui/icons-material/HomeOutlined";
import SettingsIcon from "@mui/icons-material/Settings";
import ApartmentIcon from "@mui/icons-material/Apartment";
import ReceiptIcon from "@mui/icons-material/Receipt";
import PeopleIcon from "@mui/icons-material/People";
import BusinessIcon from "@mui/icons-material/Business";
import HomeWorkIcon from "@mui/icons-material/HomeWork";
import GroupsIcon from "@mui/icons-material/Groups";
import MenuOutlinedIcon from "@mui/icons-material/MenuOutlined";
import ScheduleIcon from "@mui/icons-material/Schedule";
import AssessmentIcon from "@mui/icons-material/Assessment";
import CorporateFareIcon from "@mui/icons-material/CorporateFare";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import SupportAgentIcon from "@mui/icons-material/SupportAgent";
import PrivacyTipIcon from "@mui/icons-material/PrivacyTip";
import SwitchAccountIcon from "@mui/icons-material/SwitchAccount";
import PaidIcon from "@mui/icons-material/Paid";
import HandshakeIcon from "@mui/icons-material/Handshake";
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";
import DescriptionIcon from "@mui/icons-material/Description";
import FactCheckIcon from "@mui/icons-material/FactCheck";


const Item = ({ title, to, icon, selected, setSelected, isCollapsed }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  return (
    <Tooltip disableHoverListener={!isCollapsed} title={title} placement="right">
      <MenuItem
        active={selected === title}
        style={{
          color: colors.primary[10],
          marginBottom: "1px",
        }}
        onClick={() => setSelected(title)}
        icon={icon}
      >
        <Typography sx={{ fontSize: { xs: "14px", md: "15px" } }} fontWeight={600}>
          {title}
        </Typography>
        <Link to={to} />
      </MenuItem>
    </Tooltip>
  );
};

const SIDEBAR_LABEL = {
  admin: "ADMIN",
  manager: "MANAGER",
  relationship_manager: "RM",
  broker: "BROKER",
};

const Sidebar = ({ isCollapsed, handleIsCollapsed }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const isNonMobileScreens = useMediaQuery("(min-width: 900px)");
  const [selected, setSelected] = useState("Dashboard");
  const roleFromState = useSelector((state) => state.role);
  const isAdminFlag = useSelector((state) => state.isAdmin);
  const role = roleFromState || (isAdminFlag === 1 ? "admin" : "manager");

  const sideBarPosition = isNonMobileScreens ? "sticky" : "absolute";
  const showSideBar = !isNonMobileScreens && isCollapsed ? { display: "none" } : {};
  const isAdmin = role === "admin";
  const isManager = role === "manager";
  const isRM = role === "relationship_manager";
  const isBroker = role === "broker";

  return (
    <Box
      sx={{
        position: sideBarPosition,
        height: "100vh",
        "& .pro-sidebar-inner": {
          background: `${colors.primary[10]} !important`,
        },
        "& .pro-icon-wrapper": {
          backgroundColor: "transparent !important",
        },
        "& .pro-inner-item": {
          padding: { xs: "5px 15px 5px 10px !important", sm: "5px 35px 5px 20px !important" },
        },
        "& .pro-menu-item": {
          color: "#333 !important",
        },
        "& .pro-inner-item:hover": {
          color: "#FCB200 !important",
        },
        "& .pro-menu-item.active": {
          color: "#FCB200 !important",
        },
        borderRight: "1px solid #dfdede;",
        ...showSideBar,
      }}
    >
      <ProSidebar collapsed={isCollapsed}>
        <Menu iconShape="square">
          <MenuItem
            onClick={() => handleIsCollapsed()}
            icon={isCollapsed ? <MenuOutlinedIcon /> : undefined}
            style={{
              margin: "10px 0 20px 0",
              color: colors.grey[10],
            }}
          >
            {!isCollapsed && (
              <Box display="flex" justifyContent="space-between" alignItems="center" ml="15px">
                <Typography variant="h4" color={colors.grey[10]}>
                  {SIDEBAR_LABEL[role] ?? "ADMIN"}
                </Typography>
                <IconButton onClick={() => handleIsCollapsed()}>
                  <MenuOutlinedIcon sx={{ color: colors.grey[10] }} />
                </IconButton>
              </Box>
            )}
          </MenuItem>

          <Box paddingLeft={isCollapsed ? undefined : "1%"}>
            {isBroker ? (
              <>
                <Item title="Commissions Due" to="/broker-commissions" icon={<PaidIcon />} selected={selected} setSelected={setSelected} isCollapsed={isCollapsed} />
                <Item title="Assigned Landlords" to="/broker-landlords" icon={<HandshakeIcon />} selected={selected} setSelected={setSelected} isCollapsed={isCollapsed} />
                <Item title="Payment History" to="/broker-payments" icon={<ReceiptIcon />} selected={selected} setSelected={setSelected} isCollapsed={isCollapsed} />
              </>
            ) : (
              <>
            <Item title="Dashboard" to="/home" icon={<HomeOutlinedIcon />} selected={selected} setSelected={setSelected} isCollapsed={isCollapsed} />

            {isAdmin && (
              <>
                <Item title="Organisations" to="/organisations" icon={<CorporateFareIcon />} selected={selected} setSelected={setSelected} isCollapsed={isCollapsed} />
                <Item title="Reallocate Landlords" to="/reallocate-landlords" icon={<SwapHorizIcon />} selected={selected} setSelected={setSelected} isCollapsed={isCollapsed} />
                <Item title="Payment Charges" to="/payment-charges" icon={<PaidIcon />} selected={selected} setSelected={setSelected} isCollapsed={isCollapsed} />
                <Item title="Agreement Templates" to="/agreement-templates" icon={<DescriptionIcon />} selected={selected} setSelected={setSelected} isCollapsed={isCollapsed} />
                <Item title="Notifications" to="/notifications" icon={<NotificationsActiveIcon />} selected={selected} setSelected={setSelected} isCollapsed={isCollapsed} />
                <Item title="Audit & Approvals" to="/audit-approvals" icon={<FactCheckIcon />} selected={selected} setSelected={setSelected} isCollapsed={isCollapsed} />
                <Item title="Bookings" to="/bookings" icon={<HomeWorkIcon />} selected={selected} setSelected={setSelected} isCollapsed={isCollapsed} />
                <Item title="Reports" to="/reports" icon={<AssessmentIcon />} selected={selected} setSelected={setSelected} isCollapsed={isCollapsed} />
              </>
            )}

            {!isAdmin && (
              <>
                <Item title="Landlords" to="/landlords" icon={<BusinessIcon />} selected={selected} setSelected={setSelected} isCollapsed={isCollapsed} />
                <Item title="Properties" to="/generalProperties" icon={<HomeWorkIcon />} selected={selected} setSelected={setSelected} isCollapsed={isCollapsed} />

                {isManager && (
                  <>
                    <Item title="Property Manager" to="/managment" icon={<ApartmentIcon />} selected={selected} setSelected={setSelected} isCollapsed={isCollapsed} />
                    <Item title="Relationship Managers" to="/relationship-managers" icon={<SupportAgentIcon />} selected={selected} setSelected={setSelected} isCollapsed={isCollapsed} />
                  </>
                )}

                {isManager && (
                  <Item title="Users" to="/tenants" icon={<GroupsIcon />} selected={selected} setSelected={setSelected} isCollapsed={isCollapsed} />
                )}
                <Item title="Payments" to="/payments" icon={<ReceiptIcon />} selected={selected} setSelected={setSelected} isCollapsed={isCollapsed} />
                <Item title="Audit & Approvals" to="/audit-approvals" icon={<FactCheckIcon />} selected={selected} setSelected={setSelected} isCollapsed={isCollapsed} />

                {isManager && (
                  <>
                    <Item title="Bookings" to="/bookings" icon={<HomeWorkIcon />} selected={selected} setSelected={setSelected} isCollapsed={isCollapsed} />
                    <Item title="Active Schedules" to="/schedules" icon={<PeopleIcon />} selected={selected} setSelected={setSelected} isCollapsed={isCollapsed} />
                    <Item title="Anonymous Tenancies" to="/anonymous-tenancies" icon={<PrivacyTipIcon />} selected={selected} setSelected={setSelected} isCollapsed={isCollapsed} />
                    <Item title="Anonymous Landlords" to="/anonymous-landlords" icon={<SwitchAccountIcon />} selected={selected} setSelected={setSelected} isCollapsed={isCollapsed} />
                  </>
                )}

                <Item title="Reports" to="/reports" icon={<AssessmentIcon />} selected={selected} setSelected={setSelected} isCollapsed={isCollapsed} />
                {isManager && (
                  <Item title="Broker Commissions" to="/broker-commission-payouts" icon={<PaidIcon />} selected={selected} setSelected={setSelected} isCollapsed={isCollapsed} />
                )}
                {isManager && (
                  <Item title="Settings" to="/settings" icon={<SettingsIcon />} selected={selected} setSelected={setSelected} isCollapsed={isCollapsed} />
                )}

                {isManager && (
                  <Item title="Completed Schedules" to="/completed-schedules" icon={<ScheduleIcon />} selected={selected} setSelected={setSelected} isCollapsed={isCollapsed} />
                )}
              </>
            )}
              </>
            )}
          </Box>
        </Menu>
      </ProSidebar>
    </Box>
  );
};

export default Sidebar;
